import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useListBrowserUtils } from '@ts-private/list-browser';
import { documentSyncStatesStore } from './store';
import WrappedFakeText from 'components/common/WrappedFakeText';
import ItemComponent from './ItemComponent';
import useQuery from 'hooks/apollo/useQuery';
import { DICTIONARY_SYNC_TYPE_QUERY, DOCUMENT_STATE_X_TYPE_QUERY } from '../queries';
import uniqBy from 'lodash.uniqby';
import FlexBox from 'components/common/FlexBox';
import { filterDocumentSyncStatesStore } from './BrowserComponent';
import { sortBy } from 'utils/functions';
import { Typography } from '@totalsoft/rocket-ui';
import Portal from 'utils/portal/Portal';
import { withAreaGetter } from 'providers/AreasConsumers';
import { areas } from 'constants/areas';
import IconButton from 'components/common/buttons/IconButton';
import { useTranslation } from 'react-i18next';

const ItemsComponent = ({ getElemByAreaKey }) => {
  const { t } = useTranslation();
  const { store } = useListBrowserUtils(documentSyncStatesStore);

  const [values, setValues] = useState([]);
  const valuesRef = useRef(null);
  const [newValue, setNewValue] = useState(null);

  const [valuesPopulated, setValuesPopulated] = useState(false);

  useEffect(() => {
    if (!store.initialized || store.loading) return;
    setValues([...store.values]);
    setValuesPopulated(true);
  }, [store.initialized, store.values, store.loading]);

  useEffect(() => {
    if (!store.initialized || store.loading) return;
    valuesRef.current = [...store.allValues];
  }, [store.allValues, store.initialized, store.loading]);

  const updateValueRef = useCallback((id, updatedProperties) => {
    valuesRef.current = valuesRef.current.map(value => {
      if (value.documentSyncStateId !== id) return value;
      return { ...value, ...updatedProperties };
    });
  }, []);

  // #region Load collections

  const { loading: documentStatesXTypeLoading, data: documentStatesXTypeData } = useQuery(DOCUMENT_STATE_X_TYPE_QUERY);

  const { data: dictionarySyncTypeData } = useQuery(DICTIONARY_SYNC_TYPE_QUERY);

  const documentStatesXType = useMemo(
    () =>
      Object.values(documentStatesXTypeData?.documentStatesXType || []).sort((a, b) =>
        a.documentStateName > b.documentStateName ? 1 : -1
      ),
    [documentStatesXTypeData]
  );

  const documentTypes = useMemo(() => {
    const documentTypes = uniqBy(
      documentStatesXType.map(x => ({
        code: x.documentTypeCode,
        name: x.documentTypeName,
        isExternal: x.isExternal
      })),
      'code'
    );
    documentTypes.sort((a, b) => a.name.localeCompare(b.name));
    return documentTypes;
  }, [documentStatesXType]);

  const dictionarySyncTypes = useMemo(
    () =>
      (dictionarySyncTypeData?.dictionarySyncType || []).map(({ dictionaryItemId, dictionaryItemCode, dictionaryItemName }) => ({
        id: dictionaryItemId,
        code: dictionaryItemCode,
        name: dictionaryItemName
      })),
    [dictionarySyncTypeData]
  );

  // #endregion

  // #region Filter

  const filterDocumentSyncStates = useCallback(
    (data, pager, filters) => {
      if (documentStatesXTypeLoading) return data;
      const _data = !valuesRef.current || valuesRef.current.length !== data.length ? data : valuesRef.current;
      let items = _data.map(value => ({
        ...value,
        sourceDocumentTypeName: documentTypes.find(ds => ds.code === value.sourceDocumentTypeCode)?.name,
        destinationDocumentTypeName: documentTypes.find(ds => ds.code === value.destinationDocumentTypeCode)?.name
      }));

      if (filters.search) {
        items = items.filter(item => {
          return (
            item.sourceDocumentTypeName?.toLowerCase()?.trim()?.includes(filters.search.toLowerCase().trim()) ||
            item.destinationDocumentTypeName?.toLowerCase()?.trim()?.includes(filters.search.toLowerCase().trim())
          );
        });
      }

      if (filters.sourceDocumentType)
        items = items.filter(item => {
          return item.sourceDocumentTypeName?.toLowerCase()?.trim()?.includes(filters.sourceDocumentType.toLowerCase().trim());
        });

      if (filters.destinationDocumentType)
        items = items.filter(item => {
          return item.destinationDocumentTypeName?.toLowerCase()?.trim()?.includes(filters.destinationDocumentType.toLowerCase().trim());
        });

      const orderBy = [];
      if (pager.orderBy) orderBy.push(pager.orderBy);
      else orderBy.push('sourceDocumentTypeName');

      sortBy(items, orderBy, pager.direction);

      return items.map(item => data.find(d => d.documentSyncStateId === item.documentSyncStateId)).filter(item => Boolean(item));
    },
    [documentTypes, documentStatesXTypeLoading]
  );

  useEffect(() => {
    filterDocumentSyncStatesStore.set(filterDocumentSyncStates);
  }, [filterDocumentSyncStates]);

  // #endregion

  const handleAdd = useCallback(() => {
    setNewValue({
      destinationDocumentTypeCode: null,
      destinationFinalStateCode: null,
      destinationInitialStateCode: null,
      documentSyncStateId: null,
      documentSyncTypeId: null,
      sourceDocumentTypeCode: null,
      sourceStateCode: null
    });
  }, []);

  const loading = useMemo(
    () => !valuesPopulated || !store.initialized || store.loading,
    [valuesPopulated, store.initialized, store.loading]
  );

  const existingIds = useMemo(() => values.map(x => x.documentSyncStateId), [values])

  return (
    <FlexBox my={2} column gap={2}>
      {newValue && (
        <ItemComponent
          data={newValue}
          updateValueRef={updateValueRef}
          documentStatesXType={documentStatesXType}
          documentTypes={documentTypes}
          dictionarySyncTypes={dictionarySyncTypes}
          onDelete={() => setNewValue(null)}
          existingIds={existingIds}
        />
      )}
      {loading && <WrappedFakeText />}
      {!loading &&
        values.length > 0 &&
        values.map(value => {
          const valueRef = valuesRef.current?.find(v => v.documentSyncStateId === value.documentSyncStateId);
          return (
            <ItemComponent
              key={value.id}
              data={valueRef}
              updateValueRef={updateValueRef}
              documentStatesXType={documentStatesXType}
              documentTypes={documentTypes}
              dictionarySyncTypes={dictionarySyncTypes}
            />
          );
        })}
      {!loading && values.length === 0 && !newValue && <Typography>{t('ListBrowser.NoEntries')}</Typography>}
      <Portal container={getElemByAreaKey(areas.headerLinks)}>
        <IconButton type='add' size='medium' iconFontSize='medium' disabled={Boolean(newValue) || loading} onClick={handleAdd} />
      </Portal>
    </FlexBox>
  );
};

ItemsComponent.propTypes = {
  getElemByAreaKey: PropTypes.func
};

export default withAreaGetter(ItemsComponent);
